<div class="flex items-center flex-wrap gap-3 px-4 pt-6 shrink-0">
  <h2 class="text-body-lg-600 text-primary-500 m-0 font-title">Addendums</h2>
  <div class="flex items-center gap-2 ml-auto flex-wrap">
    @if (effectiveTargetDate()) {
      <p-button
        [outlined]="true"
        icon="pi pi-print"
        [label]="printAddendumsButtonLabel()"
        [disabled]="dealsOptions().length === 0"
        (onClick)="openPrintAddendumDialog()"
      />
    } @else {
      <p-button
        icon="pi pi-print"
        label="Print Addendums"
        [disabled]="true"
        pTooltip="Target date is not set"
        tooltipPosition="top"
        variant="outlined"
      />
    }
    <p-button icon="pi pi-plus" label="Add Addendum" (onClick)="openAddAddendumDialog()" />
  </div>
</div>
<div class="flex flex-1 overflow-hidden gap-4 p-4">
  <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
    @if (dealsOptions().length > 0) {
      <label class="flex items-center gap-2 mb-0 select-none cursor-pointer text-surface-500 shrink-0">
        <p-checkbox
          binary="true"
          [ngModel]="isAllAddendumsSelectedForPrint()"
          (onChange)="onSelectAllPrintChange($event.checked)"
          (click)="$event.stopPropagation()"
          aria-label="Select all addendums for printing"
        ></p-checkbox>
        <span class="text-body-sm">Select all</span>
      </label>
    }
    <div class="flex flex-col flex-1 overflow-hidden mt-1">
      <div class="overflow-y-auto flex-1 flex flex-col gap-4 pt-3">
        @for (dealAddendum of dealsOptions(); track dealAddendum.id) {
          @let isEditingAddendum = dealAddendum.id === selectedAddendumId() && !selectedProductId();
          <div
            (click)="onAddendumSelect(dealAddendum.id)"
            class="addendum-card border border-slate-200 rounded-md px-2 py-4 md:p-4 cursor-pointer relative shrink-0"
            [class.addendum-editing]="isEditingAddendum"
            [ngClass]="{ 'border-slate-400': dealAddendum.isExpanded }"
          >
            <span class="absolute -top-2 left-6 z-10 flex items-center gap-1.5 whitespace-nowrap">
              <p-badge
                [value]="'#' + dealAddendum.seq"
                severity="contrast"
                size="small"
                [class]="
                  isEditingAddendum
                    ? 'addendum-seq-badge addendum-seq-badge-editing shadow-sm'
                    : 'addendum-seq-badge shadow-sm'
                "
              />
              @if (isEditingAddendum) {
                <p-tag
                  value="Editing"
                  icon="pi pi-pencil"
                  size="small"
                  severity="contrast"
                  [rounded]="false"
                  styleClass="addendum-editing-tag shadow-sm"
                />
              }
            </span>
            <div class="flex items-start gap-2.5 pr-0 md:pr-24">
              <p-checkbox
                binary="true"
                [ngModel]="isAddendumSelectedForPrint(dealAddendum.id)"
                (onChange)="onAddendumPrintToggle(dealAddendum.id, $event.checked)"
                (click)="$event.stopPropagation()"
                [attr.aria-label]="'Select addendum ' + dealAddendum.seq + ' for printing'"
              ></p-checkbox>
              <dsm-aps-addendum-preview
                (click)="$event.stopPropagation(); onAddendumSelect(dealAddendum.id)"
                class="flex-1 min-w-0 flex items-start"
                [dealAddendum]="dealAddendum"
                [isExpanded]="dealAddendum.isExpanded"
                [showSeq]="false"
                (toggleExpand)="dealAddendum.isExpanded = !dealAddendum.isExpanded"
                (onChanges)="onOptionChanges()"
              />
            </div>

            @if (dealAddendum.isExpanded) {
              <p-divider />
              <dsm-aps-addendum-options
                [selectedProductId]="selectedProductId()"
                [dealAddendum]="dealAddendum"
                [projectName]="effectiveDeal()?.projectCode ?? null"
                [lotNo]="effectiveDeal()?.lotNo"
                [modelId]="effectiveModelId()"
                [elevationId]="effectiveElevationId()"
                [modelName]="effectiveDeal()?.selectedModel?.modelName ?? null"
                [elevationLabel]="effectiveDeal()?.selectedElevation?.elevationCode ?? null"
                (onChanges)="onOptionChanges()"
                (onProductSelected)="onAddendumProductSelect(dealAddendum.id, $event)"
                (onKitProductSelected)="onAddendumProductSelect(dealAddendum.id, $event, true)"
              />
            }
          </div>
        }
      </div>
    </div>
  </div>

  @if (showAddendumDetails() || showOptionDetails()) {
    <div
      [class]="
        isMobile()
          ? 'fixed inset-x-3 bottom-3 top-14 z-50 overflow-hidden rounded-xl'
          : 'w-80 xl:w-96 shrink-0 overflow-y-auto overflow-x-hidden self-start h-full'
      "
    >
      @if (showAddendumDetails()) {
        <dsm-aps-addendum-details
          [dealAddendum]="selectedAddendum()!"
          [isUnderContract]="isUnderContract()"
          (onChanges)="onOptionChanges()"
          (onClose)="closeMobileDetails()"
        />
      }
      @if (showOptionDetails()) {
        <dsm-aps-addendum-option-details
          [dealAddendum]="selectedAddendum()!"
          [addendumProductId]="selectedProductId()!"
          (onChanges)="onOptionChanges()"
          [isKitLinkedProduct]="selectedKitProductId === selectedProductId()"
          (onClose)="closeMobileDetails()"
        />
      }
    </div>
    @if (isMobile()) {
      <div class="fixed inset-0 bg-black/40 z-40" (click)="closeMobileDetails()"></div>
    }
  }
</div>

<p-dialog
  header="Add New Addendum"
  [visible]="addAddendumDialogVisible()"
  (visibleChange)="addAddendumDialogVisible.set($event)"
  [modal]="true"
  [style]="{ width: '32rem' }"
  [appendTo]="'body'"
>
  @if (addAddendumDialogVisible()) {
    <dsm-add-addendum-modal
      [lotId]="effectiveLotId()"
      [dealId]="effectiveDealId()"
      (saved)="onAddendumSaved()"
      (dialogClose)="addAddendumDialogVisible.set(false)"
    />
  }
</p-dialog>

<p-dialog
  header="Print Addendum Options"
  [visible]="printAddendumDialogVisible()"
  (visibleChange)="onPrintDialogVisibilityChange($event)"
  [modal]="true"
  [style]="{ width: '34rem' }"
>
  <div class="flex flex-col gap-3">
    @for (option of printAddendumOptionsResource.value(); track option.id) {
      <label class="flex items-center gap-2">
        <p-checkbox
          [binary]="true"
          [ngModel]="isPrintOptionSelected(option.name)"
          (onChange)="onPrintOptionToggle(option.name, !!$event.checked)"
        />
        <span>{{ option.label }}</span>
      </label>
    }
    @if (printAddendumOptionsResource.value().length === 0) {
      <p class="m-0 text-sm text-gray-500">No print options found.</p>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-end gap-2">
      <p-button label="Cancel" [outlined]="true" (onClick)="onPrintDialogVisibilityChange(false)" />
      <p-button label="Print" (onClick)="submitPrintAddendums()" />
    </div>
  </ng-template>
</p-dialog>
